import CTNDropdown from '@ctn/ui/components/ctn-dropdown';
import CTNInput from '@ctn/ui/components/ctn-input';
import CTNRadioButton from '@ctn/ui/components/ctn-radio-button';
import { type CTNDropdownOptionProps } from '@ctn/ui/types/ctn-dropdown';
import { type CTNRadioButtonProps } from '@ctn/ui/types/ctn-radio-button';
import classNames from 'classnames';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import useDireccion from '@/hooks/useDireccion';
import useOptionsStore from '@/store/options.store';

import { TIPO_DIRECCION } from '@/constants/tramite.constants';
import { Cleaners } from '@/helpers/Cleaners/fieldDataCleaner';

import { getDefaultValues, type SujetoForm } from '../config';

type Props = {
	readonly className?: string;
	readonly title: string;
	readonly isDisplay?: boolean;
	readonly isDisabled?: boolean;
};
const Direccion = ({ className, title, isDisplay, isDisabled }: Props) => {
	const context = useFormContext<SujetoForm>();
	const { control, formState, watch, setValue } = context;
	const { municipios, handleCodigoPostal, disabledMunicipio } = useDireccion(context);

	const errors = formState.errors.direccion;

	const idTipoDireccion = watch('direccion.idTipoDireccion');
	const isEspanola = idTipoDireccion === '1';
	const isExtranjero = idTipoDireccion === '2';

	const paises = useOptionsStore((state) => state.options.paises) ?? [];
	const tipoVia = useOptionsStore((state) => state.options.tipoVia) ?? [];
	const provincias = useOptionsStore((state) => state.options.provincias) ?? [];

	const resetDireccion = (value: string) => {
		setValue('direccion', { ...getDefaultValues().direccion, idTipoDireccion: value });
	};

	const classNameDireccion = useMemo(() => classNames(className), [className]);

	const optionsTipoDireccion = [
		{ label: 'Sin dirección', value: String(TIPO_DIRECCION.SIN_DIRECCION), disabled: isDisabled },
		{ label: 'Dirección Española', value: String(TIPO_DIRECCION.NACIONAL), disabled: isDisabled },
		{ label: 'Dirección Extranjera', value: String(TIPO_DIRECCION.EXTRANJERA), disabled: isDisabled },
	];

	const addDescription =
		({ options, nameDesc }: { options: CTNDropdownOptionProps[]; nameDesc: keyof SujetoForm['direccion'] }) =>
			(e: CTNDropdownOptionProps | CTNDropdownOptionProps[] | undefined) => {
				if (!e || Array.isArray(e)) return;

				const { value } = e;
				const description = options.find((option) => option.value === value)?.label;
				setValue(`direccion.${nameDesc}`, description);
			};

	return (
		<fieldset className={classNameDireccion}>
			<legend className="c-pnp-heading-h4">{title}</legend>
			<div
				className={classNames({
					['[ c-pnp-form-group c-pnp-form-group--display-only ] [ u-flow ]']: isDisplay,
					['[ u-mt-16 ]']: title,
				})}
			>
				{!isDisplay && (

					<CTNRadioButton
						required={!isDisplay}
						field={{ label: 'Tipo de dirección' }}
						name="direccion.idTipoDireccion"
						items={optionsTipoDireccion}
						layout="inline"
						columns={3}
						control={control}
						radioButton={{
							bgColor: 'white-100',
							color: 'blue-50',
							filled: false,
							large: true,
						}}
						error={errors?.idTipoDireccion?.message}
						onChange={resetDireccion as CTNRadioButtonProps<any>['onChange']}
					/>

				)}
				{isEspanola && (
					<>
						<div className="c-pnp-flow-grid">
							{isDisplay && (
								<CTNDropdown
									displayOnly
									noFeedBack={isDisplay}
									condensed={isDisplay}
									options={optionsTipoDireccion}
									classNameField="c-pnp-flow-grid__ubi"
									name="direccion.idTipoDireccion"
									label="Ubicación"
									control={control}
									appendTo='body'
								/>
							)}
							<CTNDropdown
								filter
								required={!isDisplay}
								displayOnly={isDisplay}
								condensed={isDisplay}
								noFeedBack={isDisplay}
								disabled={isDisabled}
								options={tipoVia}
								appendTo='body'
								classNameField="c-pnp-flow-grid__tipvia"
								name="direccion.codTipoVia"
								label="Tipo de vía"
								control={control}
								error={errors?.codTipoVia?.message}
								onChange={addDescription({ nameDesc: 'descripcionTipoVia', options: tipoVia })}
							/>
							<CTNInput
								required={!isDisplay}
								displayOnly={isDisplay}
								condensed={isDisplay}
								noFeedBack={isDisplay}
								disabled={isDisabled}
								type="text"
								classNameField="c-pnp-flow-grid__nomvia"
								name="direccion.nombreVia"
								label="Nombre de la vía"
								control={control}
								maxLength={150}
								error={errors?.nombreVia?.message}
								onBlur={(e) => {
									const limpio = Cleaners.via(e.target.value);
									e.target.value = limpio;
									setValue('direccion.nombreVia', limpio);
								}}
								onChange={(e) => {
									const limpio = Cleaners.via(e.target.value);
									e.target.value = limpio;
									setValue('direccion.nombreVia', limpio);
								}}
							/>
						</div>
						<div className="c-pnp-flow-grid">
							<CTNInput
								required={!isDisplay}
								displayOnly={isDisplay}
								condensed={isDisplay}
								noFeedBack={isDisplay}
								disabled={isDisabled}
								type="text"
								classNameField="c-pnp-flow-grid__numvia"
								name="direccion.numeroVia"
								label="Número"
								control={control}
								maxLength={10}
								error={errors?.numeroVia?.message}
								onBlur={(e) => {
									const limpio = Cleaners.alfanumericoSimple(e.target.value);
									e.target.value = limpio;
									setValue('direccion.numeroVia', limpio);
								}}
								onChange={(e) => {
									const limpio = Cleaners.alfanumericoSimple(e.target.value);
									e.target.value = limpio;
									setValue('direccion.numeroVia', limpio);
								}}
							/>
							<CTNInput
								displayOnly={isDisplay}
								condensed={isDisplay}
								noFeedBack={isDisplay}
								disabled={isDisabled}
								type="text"
								classNameField="c-pnp-flow-grid__blo"
								name="direccion.bloque"
								label="Bloque"
								control={control}
								maxLength={4}
								error={errors?.bloque?.message}
								onBlur={(e) => {
									const limpio = Cleaners.alfanumericoSimple(e.target.value);
									e.target.value = limpio;
									setValue('direccion.bloque', limpio);
								}}
								onChange={(e) => {
									const limpio = Cleaners.alfanumericoSimple(e.target.value);
									e.target.value = limpio;
									setValue('direccion.bloque', limpio);
								}}
							/>
							<CTNInput
								displayOnly={isDisplay}
								condensed={isDisplay}
								noFeedBack={isDisplay}
								disabled={isDisabled}
								type="text"
								classNameField="c-pnp-flow-grid__esc"
								name="direccion.escalera"
								label="Escalera"
								control={control}
								maxLength={2}
								error={errors?.escalera?.message}
								onBlur={(e) => {
									const limpio = Cleaners.alfanumericoSimple(e.target.value);
									e.target.value = limpio;
									setValue('direccion.escalera', limpio);
								}}
								onChange={(e) => {
									const limpio = Cleaners.alfanumericoSimple(e.target.value);
									e.target.value = limpio;
									setValue('direccion.escalera', limpio);
								}}
							/>
							<CTNInput
								displayOnly={isDisplay}
								condensed={isDisplay}
								noFeedBack={isDisplay}
								disabled={isDisabled}
								type="text"
								classNameField="c-pnp-flow-grid__pis"
								name="direccion.piso"
								label="Piso"
								control={control}
								maxLength={5}
								error={errors?.piso?.message}
								onBlur={(e) => {
									const limpio = Cleaners.alfanumericoSimple(e.target.value);
									e.target.value = limpio;
									setValue('direccion.piso', limpio);
								}}
								onChange={(e) => {
									const limpio = Cleaners.alfanumericoSimple(e.target.value);
									e.target.value = limpio;
									setValue('direccion.piso', limpio);
								}}
							/>
							<CTNInput
								displayOnly={isDisplay}
								condensed={isDisplay}
								noFeedBack={isDisplay}
								disabled={isDisabled}
								type="text"
								classNameField="c-pnp-flow-grid__pue"
								name="direccion.puerta"
								label="Puerta"
								control={control}
								maxLength={3}
								error={errors?.puerta?.message}
								onBlur={(e) => {
									const limpio = Cleaners.alfanumericoSimple(e.target.value);
									e.target.value = limpio;
									setValue('direccion.puerta', limpio);
								}}
								onChange={(e) => {
									const limpio = Cleaners.alfanumericoSimple(e.target.value);
									e.target.value = limpio;
									setValue('direccion.puerta', limpio);
								}}
							/>
						</div>
						<div className="c-pnp-flow-grid">
							<CTNInput
								required={!isDisplay}
								displayOnly={isDisplay}
								condensed={isDisplay}
								noFeedBack={isDisplay}
								disabled={isDisabled}
								type="text"
								classNameField="c-pnp-flow-grid__cpo"
								name="direccion.codigoPostal"
								label="C. Postal"
								control={control}
								maxLength={5}
								error={errors?.codigoPostal?.message}
								onBlur={(e) => {
									const limpio = Cleaners.numerico(e.target.value);
									e.target.value = limpio;
									setValue('direccion.codigoPostal', limpio);
									handleCodigoPostal(limpio);
								}}
								onChange={(e) => {
									const limpio = Cleaners.numerico(e.target.value);
									e.target.value = limpio;
									setValue('direccion.codigoPostal', limpio);
									handleCodigoPostal(limpio);
								}}
							/>
							<CTNDropdown
								disabled
								filter
								required={!isDisplay}
								displayOnly={isDisplay}
								condensed={isDisplay}
								noFeedBack={isDisplay}
								options={provincias}
								classNameField="c-pnp-flow-grid__pro"
								name="direccion.codProvincia"
								label="Provincia"
								appendTo='body'
								control={control}
								error={errors?.codProvincia?.message}
								onChange={addDescription({
									nameDesc: 'descripcionProvincia',
									options: provincias,
								})}
							/>
							<CTNDropdown
								filter
								required={!isDisplay}
								displayOnly={isDisplay}
								condensed={isDisplay}
								noFeedBack={isDisplay}
								disabled={disabledMunicipio || isDisabled}
								options={municipios}
								classNameField="c-pnp-flow-grid__mun"
								name="direccion.codigoMunicipio"
								label="Municipio"
								appendTo='body'
								control={control}
								error={errors?.codigoMunicipio?.message}
								onChange={addDescription({
									nameDesc: 'descripcionMunicipio',
									options: municipios,
								})}
							/>
						</div>
					</>
				)}

				{isExtranjero && (
					<>
						<div className="c-pnp-flow-grid">
							{isDisplay && (
								<CTNDropdown
									displayOnly
									noFeedBack={isDisplay}
									condensed={isDisplay}
									options={optionsTipoDireccion}
									classNameField="c-pnp-flow-grid__exttipo"
									name="direccion.idTipoDireccion"
									label="Ubicación"
									control={control}
									appendTo='body'
								/>
							)}
							<CTNDropdown
								filter
								required={!isDisplay}
								displayOnly={isDisplay}
								condensed={isDisplay}
								noFeedBack={isDisplay}
								disabled={isDisabled}
								options={paises}
								appendTo='body'
								classNameField="c-pnp-flow-grid__extpais"
								name="direccion.codigoPais"
								label="País"
								control={control}
								error={errors?.codigoPais?.message}
								onChange={addDescription({ nameDesc: 'descripcionPais', options: paises })}
							/>
						</div>
						<div className="c-pnp-flow-grid">
							<CTNInput
								displayOnly={isDisplay}
								condensed={isDisplay}
								noFeedBack={isDisplay}
								disabled={isDisabled}
								type="text"
								classNameField="c-pnp-flow-grid__extcpo"
								name="direccion.codigoPostalExtranjero"
								label="C. Postal"
								control={control}
								maxLength={15}
								error={errors?.codigoPostalExtranjero?.message}
							/>
							<CTNInput
								displayOnly={isDisplay}
								condensed={isDisplay}
								noFeedBack={isDisplay}
								disabled={isDisabled}
								type="text"
								classNameField="c-pnp-flow-grid__extprov"
								name="direccion.estadoProvinciaExtranjero"
								label="Provincia"
								control={control}
								maxLength={255}
								error={errors?.estadoProvinciaExtranjero?.message}
								onBlur={(e) => {
									const limpio = Cleaners.texto(e.target.value);
									e.target.value = limpio;
									setValue('direccion.estadoProvinciaExtranjero', limpio);
								}}
								onChange={(e) => {
									const limpio = Cleaners.texto(e.target.value);
									e.target.value = limpio;
									setValue('direccion.estadoProvinciaExtranjero', limpio);
								}}
							/>
							<CTNInput
								displayOnly={isDisplay}
								condensed={isDisplay}
								noFeedBack={isDisplay}
								disabled={isDisabled}
								type="text"
								classNameField="c-pnp-flow-grid__extmun"
								name="direccion.municipioExtranjero"
								label="Municipio"
								control={control}
								maxLength={255}
								error={errors?.municipioExtranjero?.message}
								onBlur={(e) => {
									const limpio = Cleaners.texto(e.target.value);
									e.target.value = limpio;
									setValue('direccion.municipioExtranjero', limpio);
								}}
								onChange={(e) => {
									const limpio = Cleaners.texto(e.target.value);
									e.target.value = limpio;
									setValue('direccion.municipioExtranjero', limpio);
								}}
							/>
						</div>
						<div className="c-pnp-flow-grid">
							<CTNInput
								required={!isDisplay}
								displayOnly={isDisplay}
								condensed={isDisplay}
								noFeedBack={isDisplay}
								disabled={isDisabled}
								type="text"
								classNameField="c-pnp-flow-grid__extnomvia"
								name="direccion.nombreVia"
								label="Nombre de la vía"
								control={control}
								maxLength={150}
								error={errors?.nombreVia?.message}
								onBlur={(e) => {
									const limpio = Cleaners.via(e.target.value);
									e.target.value = limpio;
									setValue('direccion.nombreVia', limpio);
								}}
								onChange={(e) => {
									const limpio = Cleaners.via(e.target.value);
									e.target.value = limpio;
									setValue('direccion.nombreVia', limpio);
								}}
							/>
							<CTNInput
								displayOnly={isDisplay}
								condensed={isDisplay}
								noFeedBack={isDisplay}
								disabled={isDisabled}
								type="text"
								classNameField="c-pnp-flow-grid__extnumvia"
								name="direccion.numeroVia"
								label="Número"
								control={control}
								maxLength={10}
								error={errors?.numeroVia?.message}
							/>
						</div>
						<div className="c-pnp-flow-grid">
							<CTNInput
								displayOnly={isDisplay}
								condensed={isDisplay}
								noFeedBack={isDisplay}
								disabled={isDisabled}
								type="text"
								classNameField="c-pnp-flow-grid__full"
								name="direccion.datosAmpliados"
								label="Complemento dirección"
								control={control}
								maxLength={255}
								error={errors?.datosAmpliados?.message}
								onBlur={(e) => {
									const limpio = Cleaners.complementoDireccion(e.target.value);
									e.target.value = limpio;
									setValue('direccion.datosAmpliados', limpio);
								}}
								onChange={(e) => {
									const limpio = Cleaners.complementoDireccion(e.target.value);
									e.target.value = limpio;
									setValue('direccion.datosAmpliados', limpio);
								}}
							/>
						</div>
					</>
				)}
			</div>
		</fieldset>
	);
};

export default Direccion;
